/* Lessons tab: lesson list, per-lesson AI "Suggest 5" batch panel, and the
   lesson editor form. */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, ClipboardCopy, Edit3, Image as ImageIcon, Plus, Save, Sparkles, Trash2, Upload, X,
} from "lucide-react";
import { adminApi } from "../portal/shell";
import { DraftCard } from "./DraftCard";

const COURSE_ID = "advanced-rpas-2026";

/**
 * Lint a Markdown lesson body for the exact pattern classes that used to
 * infinite-loop the runtime `inline()` parser in LessonViewer.jsx:
 *
 *   - Bare bracket text `[foo]` with no `](url)` companion → looked like a
 *     link opener, never closed.
 *   - Odd `**` count on a line → unclosed bold.
 *   - Odd bare-`*` count → unclosed italic.
 *   - Odd backtick count → orphan inline-code marker.
 *   - `![alt]` image opener with no matching `(url)`.
 *
 * The renderer is now defensive against these (see LessonViewer.jsx), so
 * bad content no longer hangs the page — it just renders the special
 * characters literally. But that's still an authoring mistake in almost
 * every case, so we surface it at save time.
 *
 * Returns { errors, warnings } arrays of { line, col, message }.
 * `errors` are the hang-class patterns; `warnings` are cosmetic hints.
 */
export function lintLessonBody(md) {
  const errors = [];
  if (!md) return { errors, warnings: [] };
  const warnings = [];
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  let inFencedCode = false; // triple-backtick fences aren't parsed inline

  for (let idx = 0; idx < lines.length; idx++) {
    const lineNo = idx + 1;
    const line = lines[idx];
    if (/^```/.test(line.trim())) { inFencedCode = !inFencedCode; continue; }
    if (inFencedCode) continue;

    // 1. Bracket citations: strip valid `[text](url)` first, then any
    //    lingering `[` on the line is a bare bracket the parser can't
    //    consume. Same for the image variant `![alt](url)`.
    const stripped = line
      .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
      .replace(/\[[^\]]+\]\([^)]+\)/g, "");
    const stray = stripped.indexOf("[");
    if (stray >= 0) {
      // Find the specific bracket text to name it in the error msg.
      const m = stripped.slice(stray).match(/^\[([^\]\n]{1,60})\]/);
      const sample = m ? `[${m[1]}]` : "[…]";
      errors.push({
        line: lineNo,
        col: stray + 1,
        message: `Bare bracket citation ${sample} — Markdown link syntax is [text](url). Either add a URL or unescape the brackets.`,
      });
    }
    // Orphan `!` immediately followed by `[` but not closed as ![alt](url).
    // Only fire if there is a `![` on the line that DIDN'T match a full
    // image; skip lines where every `![` did.
    const badImg = stripped.match(/!\[[^\]]*\]/);
    if (badImg) {
      errors.push({
        line: lineNo,
        col: (stripped.indexOf("![") + 1) || 1,
        message: `Image opener \`![…]\` with no \`(url)\`. Add the asset link e.g. \`![alt](asset:diagram-slug)\`.`,
      });
    }

    // 2. Unclosed bold/italic — remove valid `**...**` pairs, then bare `*..*`
    //    pairs, then anything remaining triggers a warning.
    let scan = line
      .replace(/`[^`\n]*`/g, "") // strip inline code first (contents don't count)
      .replace(/\*\*[^*\n]+?\*\*/g, "")
      .replace(/\*[^*\n]+?\*/g, "");
    const doubleStar = scan.indexOf("**");
    if (doubleStar >= 0) {
      errors.push({
        line: lineNo,
        col: doubleStar + 1,
        message: `Unclosed \`**bold**\` marker. Every \`**\` must have a matching \`**\` on the same line.`,
      });
    } else if (scan.indexOf("*") >= 0) {
      errors.push({
        line: lineNo,
        col: scan.indexOf("*") + 1,
        message: `Unclosed \`*italic*\` marker. Every \`*\` must have a matching \`*\` on the same line.`,
      });
    }

    // 3. Orphan backticks — odd count is the tell.
    const tickCount = (line.match(/`/g) || []).length;
    if (tickCount % 2 !== 0) {
      errors.push({
        line: lineNo,
        col: line.indexOf("`") + 1,
        message: `Orphan backtick — \`inline code\` needs matched \`\` \` \`\` on the same line.`,
      });
    }

    // 4. Warnings: overlong headings (>120 chars typically indicates a
    //    missed newline after `##`) — cosmetic, doesn't break render.
    if (/^\s*#{1,3}\s/.test(line) && line.length > 120) {
      warnings.push({
        line: lineNo, col: 1,
        message: `Heading line is ${line.length} chars — likely a missing newline after \`##\`.`,
      });
    }
  }
  if (inFencedCode) {
    errors.push({
      line: lines.length,
      col: 1,
      message: "Unclosed triple-backtick code fence.",
    });
  }
  return { errors, warnings };
}

export function LessonsTab({ module, onChange }) {
  const [editing, setEditing] = useState(null); // lesson object or "new"
  const [suggesting, setSuggesting] = useState(null); // lesson currently being suggested against
  const lessons = (module.lessons || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  const remove = async (id) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      await adminApi.deleteLesson(COURSE_ID, module.id, id);
      toast.success("Lesson deleted"); onChange();
    } catch (e) { toast.error(e.message || "Failed"); }
  };

  if (editing) {
    return (
      <LessonEditor
        module={module}
        lesson={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); onChange(); }}
      />
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ color: "#a5b8b3", fontSize: 13 }}>{lessons.length} lesson(s)</span>
        <button className="tbg-btn" onClick={() => setEditing("new")} data-testid="authoring-lesson-new">
          <Plus size={13} /> New lesson
        </button>
      </div>
      {lessons.length === 0 ? (
        <div style={{ padding: 20, border: "1px dashed #2a403c", borderRadius: 4, color: "#7b8b87",
          fontSize: 13, textAlign: "center" }}>
          No lessons yet. Add your first lesson.
        </div>
      ) : (
        <div>
          {lessons.map((l) => (
            <div key={l.id} className="tbg-auth-modrow" data-testid={`authoring-lesson-row-${l.id}`}>
              <div className="code">L{l.order}</div>
              <div className="title">
                {l.title}
                <div style={{ color: "#7b8b87", fontSize: 11, marginTop: 2 }}>
                  {(l.body_md || "").slice(0, 80)}{(l.body_md || "").length > 80 ? "…" : ""}
                </div>
              </div>
              <div className="counts">{l.est_minutes ? `${l.est_minutes}m` : ""}</div>
              <button className="tbg-btn ghost" style={{ padding: "5px 10px", minWidth: 0, borderColor: "rgba(156,108,248,0.5)", color: "#c9a8ff" }}
                onClick={() => setSuggesting(l)} data-testid={`authoring-lesson-suggest-${l.id}`}
                title="Ask Claude to draft 5 diverse questions from this lesson">
                <Sparkles size={11} /> Suggest 5
              </button>
              <button className="tbg-btn ghost" style={{ padding: "5px 10px", minWidth: 0 }}
                onClick={() => setEditing(l)} data-testid={`authoring-lesson-edit-${l.id}`}>
                <Edit3 size={11} /> Edit
              </button>
              <button className="tbg-btn ghost" style={{ padding: "5px 10px", minWidth: 0 }}
                onClick={() => remove(l.id)} data-testid={`authoring-lesson-delete-${l.id}`}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {suggesting && (
        <LessonSuggesterPanel
          module={module}
          lesson={suggesting}
          onClose={() => setSuggesting(null)}
          onSaved={() => { setSuggesting(null); onChange(); }}
        />
      )}
    </div>
  );
}

function LessonSuggesterPanel({ module, lesson, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState({});
  const [saving, setSaving] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.batchDraftFromLesson(COURSE_ID, module.id, lesson.id);
        setBatch(r);
      } catch (e) {
        setErr(e.message || "Batch draft failed");
      } finally { setLoading(false); }
    })();
  }, [lesson.id, module.id]);

  const saveOne = async (i) => {
    if (saved[i] || saving[i]) return;
    setSaving((s) => ({ ...s, [i]: true }));
    const draft = batch.drafts[i];
    try {
      const created = await adminApi.createQuestion(COURSE_ID, module.id, {
        prompt: draft.prompt,
        options: draft.options,
        correct_index: draft.correct_index,
        rationale: draft.rationale,
        scope: "both",
        is_placeholder: false,
        is_ai_drafted: true,
      });
      setSaved((s) => ({ ...s, [i]: created.id }));
      toast.success(`Draft ${i + 1} saved — awaiting review`);
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving((s) => ({ ...s, [i]: false })); }
  };

  const saveAllUnsaved = async () => {
    if (bulkSaving) return;
    setBulkSaving(true);
    try {
      for (let i = 0; i < batch.drafts.length; i++) {
        if (saved[i]) continue;
        await saveOne(i);
      }
      toast.success("All unsaved drafts staged for review");
    } finally { setBulkSaving(false); }
  };

  const total = batch?.drafts?.length || 0;
  const savedCount = Object.values(saved).filter(Boolean).length;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="lesson-suggester-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 820, width: "100%", maxHeight: "92vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#9c6cf8" }}>
              <Sparkles size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Batch Draft · 5 diverse questions
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              From: {lesson.title}
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 4 }}>
              Model asked to vary angle, difficulty, and LO across the 5. Every accepted draft
              is saved with <b>is_ai_drafted=True</b> and will not reach students until you
              review and approve it.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="suggester-close">
            <X size={12} /> Close
          </button>
        </div>

        {loading && (
          <div style={{ padding: 30, textAlign: "center", color: "#c9a8ff", fontSize: 13 }}>
            <Sparkles size={14} className="spin" style={{ marginRight: 6, verticalAlign: "-2px" }} />
            Claude is drafting 5 questions… (usually 8–15 seconds)
          </div>
        )}

        {err && (
          <div className="tbg-lms-err" data-testid="suggester-err" style={{ marginBottom: 12 }}>{err}</div>
        )}

        {batch && (
          <>
            {batch.warnings && batch.warnings.length > 0 && (
              <div style={{ marginBottom: 14, padding: "10px 12px", background: "rgba(240,190,60,0.08)",
                border: "1px solid rgba(240,190,60,0.35)", color: "#f0be3c", borderRadius: 4, fontSize: 12 }}
                data-testid="suggester-warnings">
                <strong>Model raised concerns:</strong>
                <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                  {batch.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 12, font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87" }}>
              <span>{savedCount} / {total} staged as AI drafts</span>
              {savedCount < total && (
                <button className="tbg-btn" style={{ background: "#9c6cf8", padding: "6px 14px" }}
                  onClick={saveAllUnsaved} disabled={bulkSaving}
                  data-testid="suggester-save-all">
                  {bulkSaving ? "Staging…" : `Stage all remaining (${total - savedCount})`}
                </button>
              )}
            </div>

            {batch.drafts.map((d, i) => (
              <DraftCard
                key={i} draft={d} index={i}
                saved={!!saved[i]} saving={!!saving[i]}
                onSave={() => saveOne(i)}
              />
            ))}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14, gap: 8 }}>
              <button className="tbg-btn" onClick={() => { onSaved(); }} data-testid="suggester-done">
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LessonEditor({ module, lesson, onClose, onSaved }) {
  const [title, setTitle] = useState(lesson?.title || "");
  const [body, setBody] = useState(lesson?.body_md || "");
  const [refs, setRefs] = useState((lesson?.references || []).join("\n"));
  const [est, setEst] = useState(lesson?.est_minutes || "");
  const [order, setOrder] = useState(lesson?.order || (module.lessons?.length || 0) + 1);
  const [saving, setSaving] = useState(false);
  const [assets, setAssets] = useState(lesson?.assets || []);
  const bodyRef = useRef(null);

  // Live-lint the body. Errors are hang-class patterns (bare brackets,
  // unclosed bold/italic, orphan backticks). Save button will confirm
  // before shipping content with any errors.
  const lint = useMemo(() => lintLessonBody(body), [body]);

  const save = async () => {
    if (!title.trim()) return toast.error("Title required");
    if (lint.errors.length > 0) {
      const first = lint.errors[0];
      const ok = window.confirm(
        `Content lint found ${lint.errors.length} issue${lint.errors.length === 1 ? "" : "s"}.\n\n` +
        `First: line ${first.line}: ${first.message}\n\n` +
        `These patterns used to hang the student's browser (Feb 2026 Module 6 bug). ` +
        `The renderer is now defensive, but authoring intent is almost certainly wrong.\n\n` +
        `Save anyway?`,
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const body_ = {
        title: title.trim(),
        body_md: body,
        references: refs.split("\n").map((r) => r.trim()).filter(Boolean),
        est_minutes: est ? parseInt(est, 10) : null,
        order: order ? parseInt(order, 10) : null,
      };
      if (lesson) {
        await adminApi.updateLesson(COURSE_ID, module.id, lesson.id, body_);
        toast.success("Lesson saved");
      } else {
        await adminApi.createLesson(COURSE_ID, module.id, body_);
        toast.success("Lesson created");
      }
      onSaved();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const insertAtCursor = (snippet) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => `${b}${b.endsWith("\n") ? "" : "\n"}${snippet}\n`); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + snippet + body.slice(end);
    setBody(next);
    // Restore focus & put cursor after inserted snippet
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="tbg-auth-editor" data-testid="authoring-lesson-editor">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong style={{ color: "#3dc0af", letterSpacing: "0.14em", fontSize: 11, textTransform: "uppercase" }}>
          {lesson ? "Edit lesson" : "New lesson"}
        </strong>
        <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}>
          <X size={12} /> Close
        </button>
      </div>
      <label>Title</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="authoring-lesson-title" />
      <div className="row">
        <div style={{ flex: 1 }}>
          <label>Order</label>
          <input type="number" value={order} onChange={(e) => setOrder(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label>Est. minutes</label>
          <input type="number" value={est} onChange={(e) => setEst(e.target.value)} />
        </div>
      </div>
      <label>Body (Markdown — # heading, - list, **bold**, *italic*, `code`, ![alt](asset:slug) for diagrams)</label>
      <textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 260 }}
        data-testid="authoring-lesson-body" placeholder="Paste your licensed lesson content here…" />

      {(lint.errors.length > 0 || lint.warnings.length > 0) && (
        <div
          data-testid="authoring-lesson-lint"
          style={{
            marginTop: -6, marginBottom: 10, padding: "10px 12px",
            background: lint.errors.length > 0
              ? "rgba(239,106,106,0.06)" : "rgba(240,190,60,0.06)",
            border: lint.errors.length > 0
              ? "1px solid rgba(239,106,106,0.4)"
              : "1px solid rgba(240,190,60,0.4)",
            borderRadius: 4,
            font: "500 12px 'JetBrains Mono', monospace",
            color: "#e2ece9",
          }}
        >
          <div style={{
            font: "700 10px 'JetBrains Mono', monospace",
            letterSpacing: "0.2em", textTransform: "uppercase",
            color: lint.errors.length > 0 ? "#ef6a6a" : "#f0be3c",
            marginBottom: 8,
          }}>
            <AlertTriangle size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
            Markdown lint · {lint.errors.length} error{lint.errors.length === 1 ? "" : "s"}
            {lint.warnings.length > 0 && `, ${lint.warnings.length} warning${lint.warnings.length === 1 ? "" : "s"}`}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>
            {lint.errors.slice(0, 8).map((e, i) => (
              <li key={`e-${i}`} data-testid={`authoring-lesson-lint-error-${i}`}
                style={{ color: "#ef6a6a" }}>
                <span style={{ color: "#7b8b87" }}>line {e.line}:</span> {e.message}
              </li>
            ))}
            {lint.warnings.slice(0, 4).map((w, i) => (
              <li key={`w-${i}`} style={{ color: "#f0be3c" }}>
                <span style={{ color: "#7b8b87" }}>line {w.line}:</span> {w.message}
              </li>
            ))}
            {(lint.errors.length + lint.warnings.length) > 12 && (
              <li style={{ color: "#7b8b87" }}>
                …and {lint.errors.length + lint.warnings.length - 12} more.
              </li>
            )}
          </ul>
        </div>
      )}

      {lesson && (
        <LessonAssetsPanel
          lesson={lesson}
          assets={assets}
          onChangeAssets={setAssets}
          onInsertToken={insertAtCursor}
        />
      )}
      {!lesson && (
        <div style={{ padding: "10px 12px", background: "rgba(156,108,248,0.06)",
          border: "1px dashed rgba(156,108,248,0.3)", borderRadius: 4,
          color: "#a5b8b3", fontSize: 12, lineHeight: 1.5 }} data-testid="authoring-lesson-assets-hint">
          <ImageIcon size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
          Save the lesson first, then re-open it to upload diagrams.
        </div>
      )}

      <label>References (one per line)</label>
      <textarea value={refs} onChange={(e) => setRefs(e.target.value)} style={{ minHeight: 60 }}
        placeholder="TP 15263 §1.3&#10;CARs 901.62" />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
        <button className="tbg-btn ghost" onClick={onClose}>Cancel</button>
        <button className="tbg-btn" onClick={save} disabled={saving} data-testid="authoring-lesson-save">
          <Save size={13} /> {saving ? "Saving…" : "Save lesson"}
        </button>
      </div>
    </div>
  );
}


function LessonAssetsPanel({ lesson, assets, onChangeAssets, onInsertToken }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const rows = await adminApi.listLessonAssets(lesson.id);
      onChangeAssets(rows);
    } catch (e) { toast.error(e.message || "Refresh failed"); }
  };

  const onFilePicked = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error(`File too large (${Math.round(file.size / 1024)} KB). Cap is 500 KB.`);
      return;
    }
    const alt = window.prompt(
      `Alt text for "${file.name}" (describes the diagram for screen readers + caption):`,
      file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
    );
    if (alt === null) return; // user cancelled

    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = () => rej(new Error("Read failed"));
        reader.readAsDataURL(file);
      });
      const b64 = String(dataUrl).split(",", 2)[1] || "";
      const created = await adminApi.uploadLessonAsset(lesson.id, {
        filename: file.name,
        mime: file.type || "application/octet-stream",
        alt: alt.trim(),
        data_base64: b64,
      });
      onChangeAssets([...(assets || []), created]);
      toast.success(`Uploaded "${created.slug}"`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeAsset = async (slug) => {
    if (!window.confirm(`Delete asset "${slug}"?\nAny ![…](asset:${slug}) tokens in the lesson body will render as broken images.`)) return;
    try {
      await adminApi.deleteLessonAsset(lesson.id, slug);
      onChangeAssets(assets.filter((a) => a.slug !== slug));
      toast.success("Asset deleted");
    } catch (e) { toast.error(e.message || "Delete failed"); }
  };

  const copyToken = (a) => {
    const alt = a.alt || a.slug.replace(/-/g, " ");
    const token = `![${alt}](asset:${a.slug})`;
    onInsertToken(token);
    // Also copy so they can paste elsewhere if wanted
    if (navigator.clipboard) navigator.clipboard.writeText(token).catch(() => {});
    toast.success("Token inserted at cursor");
  };

  return (
    <div style={{ marginTop: 4, padding: "12px 14px", border: "1px solid #1c2a27",
      borderRadius: 4, background: "#0d1817" }} data-testid="authoring-lesson-assets">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div>
          <strong style={{ color: "#3dc0af", letterSpacing: "0.14em", fontSize: 10,
            textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>
            <ImageIcon size={12} /> Diagrams & images
          </strong>
          <div style={{ color: "#7b8b87", fontSize: 11, marginTop: 4, lineHeight: 1.5 }}>
            SVG / PNG / JPEG / WebP · max 500 KB each · 4 MB per lesson · SVGs are auto-sanitized.
            Insert into the body via <code style={{ color: "#c9a8ff" }}>![alt](asset:slug)</code>.
          </div>
        </div>
        <button className="tbg-btn" onClick={() => fileRef.current?.click()}
          disabled={uploading} data-testid="authoring-lesson-asset-upload"
          style={{ background: "#9c6cf8", padding: "6px 12px" }}>
          <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
        </button>
        <input ref={fileRef} type="file" onChange={onFilePicked} style={{ display: "none" }}
          accept="image/svg+xml,image/png,image/jpeg,image/webp"
          data-testid="authoring-lesson-asset-file-input" />
      </div>

      {(assets || []).length === 0 ? (
        <div style={{ marginTop: 12, padding: "10px 12px", color: "#7b8b87", fontSize: 12,
          border: "1px dashed #1c2a27", borderRadius: 3, textAlign: "center" }}
          data-testid="authoring-lesson-assets-empty">
          No diagrams uploaded yet.
        </div>
      ) : (
        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          {assets.map((a) => (
            <AssetRow key={a.slug} lesson={lesson} asset={a}
              onInsert={() => copyToken(a)} onDelete={() => removeAsset(a.slug)} />
          ))}
        </div>
      )}
    </div>
  );
}


function AssetRow({ lesson, asset, onInsert, onDelete }) {
  const url = adminApi.lessonAssetUrl(lesson.id, asset.slug);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 10px",
      background: "#050807", border: "1px solid #1c2a27", borderRadius: 4 }}
      data-testid={`authoring-lesson-asset-row-${asset.slug}`}>
      <img src={url} alt={asset.alt || asset.slug}
        style={{ width: 48, height: 48, objectFit: "contain", background: "#0a1413",
          borderRadius: 3, border: "1px solid #1c2a27" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ font: "600 12px 'JetBrains Mono', monospace", color: "#e2ece9",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {asset.slug}
        </div>
        <div style={{ color: "#7b8b87", fontSize: 11, marginTop: 2 }}>
          {asset.mime} · {Math.round(asset.size_bytes / 1024)} KB
          {asset.alt && <span style={{ marginLeft: 6, color: "#a5b8b3" }}>· "{asset.alt}"</span>}
        </div>
      </div>
      <button className="tbg-btn ghost" style={{ padding: "5px 10px", minWidth: 0,
        borderColor: "rgba(61,192,175,0.5)", color: "#3dc0af" }}
        onClick={onInsert} data-testid={`authoring-lesson-asset-insert-${asset.slug}`}
        title={`Insert ![…](asset:${asset.slug}) at cursor`}>
        <ClipboardCopy size={11} /> Insert
      </button>
      <button className="tbg-btn ghost" style={{ padding: "5px 10px", minWidth: 0 }}
        onClick={onDelete} data-testid={`authoring-lesson-asset-delete-${asset.slug}`}>
        <Trash2 size={11} />
      </button>
    </div>
  );
}
